import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CountryResponse } from 'com.cogvio.pricemonitor.api';
import { formatPrice, Price } from '~/libs/utils/format/price';
import { calculatePricePerPackage } from '~/libs/utils/price';
import formatPercentage from '~/utils/Intl/formatPercentage';
import { Typography } from '~/libs/ui/atoms/Typography';
import { CountrySourceWithTooltip } from '~/libs/ui/molecules/CountrySourceWithTooltip';

interface DataSourceResponse {
	shortName: string;
	description: string;
	country: CountryResponse;
}

interface TopProducts {
	calculatedPrice: Price | null;
	dataSource: DataSourceResponse;
	id: string;
	parsedPackageSize: number | null;
	referencePackageSize: number | null;
}

interface ProductCalculationProps {
	topProducts: TopProducts[];
	threeLowestPricesAvg: Price | null;
	secondAndThirdLowestPriceAvg: Price | null;
	deviation: number | null;
	exFactoryPrice: Price | null;
	emptyReason: ReactNode | null;
}

export function ProductCalculation({
	topProducts,
	threeLowestPricesAvg,
	secondAndThirdLowestPriceAvg,
	deviation,
	exFactoryPrice,
	emptyReason,
}: ProductCalculationProps) {
	return (
		<div className='w-full pt-3 pb-3 bg-gray-100'>
			{emptyReason && (
				<div className='max-w-[1260px] mx-auto px-[24px] mb-2'>
					<p className='px-3 py-2 rounded border text-md leading-snug text-red-700 border-red-500 bg-red-50 text-center tracking-wide'>
						{emptyReason}
					</p>
				</div>
			)}
			<div className='max-w-[1260px] mx-auto px-[24px] flex justify-between items-stretch gap-x-3'>
				<div className='w-80 px-4 pt-3 pb-1 rounded-md bg-white'>
					<Typography level={5} color='cPurple-600' weight='font-semibold' className='!tracking-wider'>
						Lowest ex-factory prices:
					</Typography>
					<div className='divide-y divide-gray-200'>
						{topProducts.length === 0 &&
							Array.from({ length: 3 })?.map((_, i) => (
								<div
									key={`top-product-placeholder-${i}`}
									className='w-full flex flex-nowrap items-center justify-between py-2'
								>
									<div className='relative w-0 pl-1 grow shrink flex flex-nowrap items-center justify-start'>
										<span className='absolute left-0 top-0 -ml-1 -mt-0.5 w-4 h-4 rounded-full text-[10px] leading-4 font-semibold text-gray-500 bg-white text-center z-10'>
											{i + 1}.
										</span>
										<div className='relative w-full'>
											<div className='w-auto grow shrink flex flex-nowrap justify-start items-center'>
												<i className='w-5 h-5 rounded-full bg-gray-100'></i>
												<div className='grow shrink w-0 pl-2'>
													<span className='block text-[14px] font-semibold leading-tight not-italic tracking-wider text-gray-400'>
														&nbsp;
													</span>
													<span className='block max-w-full text-[11px] leading-tight text-gray-300 tracking-wide truncate'>
														&nbsp;
													</span>
												</div>
											</div>
										</div>
									</div>
									<div className='block pl-2 grow-0 shrink-0 text-[14px] text-gray-300 font-semibold leading-tight tabular-nums tracking-wider'>
										N/A
									</div>
								</div>
							))}
						{topProducts?.map((product, i) => (
							<div
								className='w-full flex flex-nowrap items-center justify-between py-2'
								data-product-id={product.id}
								data-testid={`lowest-exfactory-price-${i}`}
								key={product.id}
							>
								<div
									className='relative w-0 pl-1 grow shrink flex flex-nowrap items-center justify-start'
									data-testid='lowest-exfactory-price-country'
								>
									<span className='absolute left-0 top-0 -ml-1 -mt-0.5 w-4 h-4 rounded-full text-[10px] leading-4 font-semibold text-gray-500 bg-white text-center z-10'>
										{i + 1}.
									</span>
									<CountrySourceWithTooltip
										countryAlpha={product.dataSource.country.alpha2}
										countryName={product.dataSource.country.name}
										dataSourceShortName={product.dataSource.shortName}
										dataSourceDescription={product.dataSource.description}
									/>
								</div>
								<div className='block pl-2 grow-0 shrink-0 text-[14px] font-semibold leading-tight tabular-nums tracking-wider'>
									{product.calculatedPrice &&
										product.parsedPackageSize &&
										product.referencePackageSize &&
										calculatePricePerPackage(
											product.calculatedPrice,
											product.parsedPackageSize,
											product.referencePackageSize
										)}
								</div>
							</div>
						))}
					</div>
				</div>
				<div className='grow shrink px-4 py-3 rounded-md bg-white'>
					<Typography level={5} color='cPurple-600' weight='font-semibold' className='!tracking-wider'>
						Calculated max price:
					</Typography>
					<div className='mt-1.5 flex flex-row items-stretch flex-wrap gap-x-2 gap-y-1.5'>
						<div className='px-3 pt-2 pb-1.5 rounded-md bg-gray-100'>
							<h3 className='block mb-0.5 text-md text-gray-700 font-normal tracking-wide'>
								Average of 3 lowest ex-factory prices:
							</h3>
							{threeLowestPricesAvg ? (
								<strong
									className='text-base text-gray-900 font-semibold tabular-nums'
									data-testid='average-of-3-lowest-ex-factory-prices'
								>
									{formatPrice(threeLowestPricesAvg)}
								</strong>
							) : (
								<strong className='text-base text-gray-400 font-semibold tracking-wide'>NaN</strong>
							)}
						</div>
						<div className='px-3 pt-2 pb-1.5 rounded-md bg-gray-100'>
							<h3 className='block mb-0.5 text-md text-gray-700 font-normal tracking-wide'>2. + 3. lowest price:</h3>
							{secondAndThirdLowestPriceAvg ? (
								<strong className='text-base text-gray-900 font-semibold tabular-nums' data-testid='2-3-lowest-price'>
									{formatPrice(secondAndThirdLowestPriceAvg)}
								</strong>
							) : (
								<strong className='text-base text-gray-400 font-semibold tracking-wide'>NaN</strong>
							)}
						</div>
						<div className='px-3 pt-2 pb-1.5 rounded-md bg-gray-100'>
							<h3 className='block mb-0.5 text-md text-gray-700 font-normal tracking-wide'>Deviation:</h3>
							{deviation ? (
								<strong className='text-base text-gray-900 font-semibold tabular-nums' data-testid='deviation'>
									{formatPercentage(deviation)}
								</strong>
							) : (
								<strong className='text-base text-gray-400 font-semibold tracking-wide'>NaN</strong>
							)}
						</div>
					</div>
					<div className='mt-2 flex flex-row items-stretch flex-wrap gap-x-2 gap-y-1.5'>
						<div className={`px-3 pt-2 pb-1.5 rounded-md ${exFactoryPrice ? 'bg-gray-100' : 'bg-red-50'}`}>
							<h3 className='block mb-0.5 text-md text-gray-700 font-normal tracking-wide'>Current price (SCAU):</h3>
							{exFactoryPrice ? (
								<strong className='text-base text-gray-900 font-semibold tabular-nums' data-testid='current-price-scau'>
									{formatPrice(exFactoryPrice)}
								</strong>
							) : (
								<p className='block mt-1 text-md leading-snug text-gray-900'>
									<strong className='block font-semibold'>No info on CZ product</strong>
									Use{' '}
									<Link
										className='text-cBlue-400 underline'
										to='/search/?currency=any&filter=country%3ACZ&operator=and&price=first-available'
									>
										search
									</Link>{' '}
									to add czech product to this group.
								</p>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
